/**
 * Default values for HTML fields per node type
 */
import {
  DEFAULT_SMS_MESSAGE_TEMPLATE,
  DEFAULT_SMS_MESSAGE_TYPE,
} from '../../../constants/smsNotification'
import type { LegacyNodeKind } from '../legacyNodes'
import { NodeType } from '../reactFlowTypes'
import { REVERSE_FIELD_NAME_MAPPINGS } from './fieldMappings'
import { NODE_TYPE_TO_KIND_MAP } from './nodeTypeMappings'

/**
 * Default values for HTML fields
 */
export const DEFAULT_HTML_VALUES = {
  simple_dial: {
    timeout: '20',
  },
  transfer_rollover: {
    timeout: '20',
  },
  auto_attendant: {
    timeout: '4',
    repeat_times: '0', // Form default per spec (not '3' from save logic)
    line_id: null,
    allow_extension_dialing: false,
  },
  wait: {
    duration: '5',
    ring_during_wait: false, // Legacy checkbox is unchecked by default (value="false" with no checked attribute)
  },
  external_number: {
    timeout: '30', // String, "30" for new nodes, "10" is fallback if empty during save
    press_1: 1, // Number, NOT string! Default is 1 (checked)
    minimized: 0, // Number, NOT string!
    confirm_call_pickup: null,
    pass_through_caller_id: true, // Boolean, default is true (checked)
  },
  call_distribution: {
    timeout: '20',
  },
  sms: {
    auto_reply: 0, // Number, NOT string!
    minimized: 0, // Number, NOT string!
  },
  change_caller_id: {
    minimized: 0, // Number, NOT string!
  },
} as const

/**
 * Required HTML fields per node type
 */
export const REQUIRED_HTML_FIELDS: Record<
  LegacyNodeKind,
  Record<string, unknown>
> = {
  start_node: {}, // No HTML
  simple_dial: { extension_id: null, timeout: '20' },
  direct_line: { line_id: null },
  transfer: { line_id: null },
  queue: { queue_id: null },
  schedule: { schedule_id: null },
  day_night: { choice_id: null },
  transfer_rollover: { transfer_rollover_id: null, timeout: '20' },
  auto_attendant: {
    sound_clip_id: null,
    timeout: '4',
    line_id: null,
    repeat_times: '0', // Form default per spec (not '3' from save logic)
    allow_extension_dialing: false,
    children: [{}],
  },
  sound_clip: { sound_clip_id: null },
  wait: { duration: '5', ring_during_wait: false }, // Legacy checkbox is unchecked by default
  hang_up: { hang_up_id: null },
  leave_voicemail: {
    extension_id: null,
    message_type: '0',
    sound_clip_id: 'null', // String "null", NOT JSON null!
  },
  dial_name: { lookup_id: null, sort_order: null },
  jump_to_dial_plan: { dial_plan_id: null },
  conference_room: { conference_room_id: null },
  external_number: {
    timeout: '30', // String, "30" for new nodes, "10" is fallback if empty during save
    confirm_call_pickup: null,
    pass_through_caller_id: true, // Boolean, default is true (checked)
    press_1: 1, // Number, NOT string! Default is 1 (checked)
    sip_server: null,
    minimized: 0, // Number, NOT string!
    transfer_company: null, // Selected company from dropdown; null when unselected
    // Note: number and phone_number_id are set conditionally in conversion
  },
  fax_detect: { fax_extension_id: null },
  webhook: { webhook_id: null, notify_url: null },
  interactive_webhook: {
    iwebhook_url: null,
    iwebhook_allow_extension_dialing: false,
    children: [{}],
  },
  call_distribution: { timeout: '20', children: [{}] },
  sms: {
    option: null,
    message: null,
    auto_reply: 0,
    sms_recipient: null,
    sms_number: null,
    minimized: 0,
  },
  change_caller_id: {
    new_caller_id: null,
    new_caller_id_option: 'six_char', // Legacy auto-selects "six_char" when creating new node, but CAN save null (when no radio checked)
    minimized: 0,
  },
  ai_agent_v1: { agent_id: null },
} as const

/**
 * Get default node data for a React Flow node type
 * Converts snake_case HTML defaults to camelCase React Flow format
 * @param nodeType - React Flow node type
 * @returns Default node data object in React Flow format
 */
export function getDefaultNodeData(
  nodeType: NodeType | 'start',
): Record<string, unknown> {
  if (nodeType === 'start') {
    return {}
  }

  // Map NodeType to LegacyNodeKind
  const kind = NODE_TYPE_TO_KIND_MAP[nodeType]
  if (!kind) {
    return {}
  }

  // Get defaults from DEFAULT_HTML_VALUES (snake_case)
  const defaults = DEFAULT_HTML_VALUES[kind as keyof typeof DEFAULT_HTML_VALUES]
  if (!defaults) {
    return {}
  }

  // Convert snake_case to camelCase using REVERSE_FIELD_NAME_MAPPINGS
  const reactFlowData: Record<string, unknown> = {}

  Object.entries(defaults).forEach(([snakeKey, value]) => {
    // Use reverse mapping if available, otherwise convert manually
    const camelKey =
      REVERSE_FIELD_NAME_MAPPINGS[snakeKey] ||
      snakeKey.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

    // Convert value types appropriately
    if (
      snakeKey === 'timeout' ||
      snakeKey === 'duration' ||
      snakeKey === 'repeat_times'
    ) {
      // Convert string numbers to numbers for React Flow
      reactFlowData[camelKey] =
        typeof value === 'string' ? Number(value) || value : value
    } else if (
      snakeKey === 'press_1' ||
      snakeKey === 'minimized' ||
      snakeKey === 'auto_reply'
    ) {
      // These are already numbers, keep as is
      reactFlowData[camelKey] = value
    } else if (snakeKey === 'pass_through_caller_id') {
      // Map to camelCase
      reactFlowData.passThroughCallerId = value
    } else if (snakeKey === 'ring_during_wait') {
      // Map to camelCase
      reactFlowData.ringDuringWait = value
    } else if (snakeKey === 'allow_extension_dialing') {
      // Map to camelCase
      reactFlowData.allowExtensionDialing = value
    } else {
      // Use reverse mapping or default conversion
      reactFlowData[camelKey] = value
    }
  })

  // Special handling for specific node types
  // Cast defaults to Record to safely access properties that may not exist on all types
  const defaultsRecord = defaults as Record<string, unknown>

  if (nodeType === NodeType.ExternalTransfer) {
    if ('press_1' in defaultsRecord) {
      reactFlowData.requireOneToConnect = Number(defaultsRecord.press_1) === 1
    }
    if ('minimized' in defaultsRecord) {
      reactFlowData.minimized = Number(defaultsRecord.minimized) === 1
    }
  } else if (nodeType === NodeType.SmsNotification) {
    if ('auto_reply' in defaultsRecord) {
      reactFlowData.autoReplyToCaller = Number(defaultsRecord.auto_reply) === 1
    }
    if ('minimized' in defaultsRecord) {
      reactFlowData.minimized = Number(defaultsRecord.minimized) === 1
    }
    // Match legacy/Admin UX expectations: new SMS nodes default to Missed call
    // with a pre-populated template so save validation passes without extra edits.
    if (!reactFlowData.messageType) {
      reactFlowData.messageType = DEFAULT_SMS_MESSAGE_TYPE
    }
    if (!reactFlowData.message) {
      reactFlowData.message = DEFAULT_SMS_MESSAGE_TEMPLATE
    }
  } else if (nodeType === NodeType.ChangeCallerId) {
    reactFlowData.newCallerIdOption = 'six_char' // Always default to six_char
    reactFlowData.mode = 'add_front' // React Flow uses 'add_front' instead of 'six_char'
    if ('minimized' in defaultsRecord) {
      reactFlowData.minimized = Number(defaultsRecord.minimized) === 1
    }
  }

  return reactFlowData
}
